// "Not an Invoice" — archive a document that isn't payable (a statement, a
// cover/routing page, a junk scan) out of the pipeline. A required reason plus
// the optional deny/archive category, then a terminal archive.
//
// PORT NOTE: this dialog used to offer a second outcome — reroute the document
// to the Claims workspace — behind a radio. Claims is not part of the product,
// so the mode picker, the claim message and the reroute callback are gone and
// the dialog does one thing.
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  Textarea,
  makeStyles,
} from '@fluentui/react-components'
import { REASON_CATEGORIES, reasonCategoryLabel } from '../lib/constants'

interface Props {
  open: boolean
  submitting?: boolean
  /** Archive as not-an-invoice (statement / cover page / junk); reason required.
      The optional category is the same deny/archive tag DecisionDialog collects
      (decisions.reason_category); undefined when none is picked. */
  onArchive: (reason: string, category?: string) => void
  onCancel: () => void
}

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: '12px' },
})

export function NotAnInvoiceDialog({ open, submitting, onArchive, onCancel }: Props) {
  const s = useStyles()
  const [reason, setReason] = useState('')
  // Optional deny/archive category KEY (e.g. 'other'), or '' for none.
  const [category, setCategory] = useState('')

  // Clear each time the dialog opens.
  useEffect(() => {
    if (open) {
      setReason('')
      setCategory('')
    }
  }, [open])

  const canConfirm = !submitting && reason.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open && !submitting) onCancel() }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>This isn’t a standard invoice</DialogTitle>
          <DialogContent>
            <div className={s.section}>
              <Field label="Reason" required>
                <Textarea
                  value={reason}
                  onChange={(_, d) => setReason(d.value)}
                  placeholder="This document is a statement. There is nothing to be paid."
                  resize="vertical"
                  rows={3}
                />
              </Field>
              <Field label="Category (optional)">
                <Dropdown
                  placeholder="Pick a category"
                  selectedOptions={category ? [category] : []}
                  value={reasonCategoryLabel(category)}
                  onOptionSelect={(_, d) => setCategory(d.optionValue ?? '')}
                >
                  {Object.entries(REASON_CATEGORIES).map(([key, label]) => (
                    <Option key={key} value={key} text={label}>
                      {label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <MessageBar intent="warning">
                <MessageBarBody>
                  This permanently removes the document from the approval pipeline — it will never be paid, and
                  it cannot be undone in the app. If it’s a real invoice, cancel and resolve its flags instead.
                </MessageBarBody>
              </MessageBar>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              style={{ backgroundColor: '#D13438' }}
              disabled={!canConfirm}
              onClick={() => onArchive(reason.trim(), category || undefined)}
            >
              {submitting ? 'Submitting…' : 'Archive'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
